import { i18n } from '@/plugins/i18n'
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import { z } from 'zod'
import { UserValidator } from './UserValidator'
import { ref } from 'vue'
import { UserRoles } from '@/models/enums/auth/UserRoles'
import type { User } from '@/models/interfaces/auth/User'

export class UserProfileForm {
  static getSchema() {
    return z.object({
      userPrenom: z
        .string({ required_error: i18n.t('forms.errorMessages.required') })
        .min(2, { message: i18n.t('forms.errorMessages.minlength', { min: 2 }) })
        .max(255, { message: i18n.t('forms.errorMessages.maxlength', { max: 255 }) }),

      userNom: z
        .string({ required_error: i18n.t('forms.errorMessages.required') })
        .min(2, { message: i18n.t('forms.errorMessages.minlength', { min: 2 }) })
        .max(255, { message: i18n.t('forms.errorMessages.maxlength', { max: 255 }) }),

      userEmail: z
        .string({ required_error: i18n.t('forms.errorMessages.required') })
        .email({ message: i18n.t('forms.errorMessages.email') }),

      ...UserValidator.passwordsObject(),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: i18n.t('auth.becomeMember.form.privacyPolicy.error'),
      }),
      userLogin: z
        .string({ required_error: i18n.t('forms.errorMessages.required') })
        .min(8, { message: i18n.t('forms.errorMessages.minlength', { min: 6 }) }),

      confirmUserLogin: z.string({ required_error: i18n.t('forms.errorMessages.required') }),
      signUpMessage: z
        .string()
        .max(500, { message: i18n.t('forms.errorMessages.maxlength', { max: 500 }) })
        .optional()
        .or(z.literal('')),
      description: z
        .string()
        .max(500, { message: i18n.t('forms.errorMessages.maxlength', { max: 500 }) })
        .optional(),
    })
  }

  static getRolesList() {
    return [
      {
        label: i18n.t('auth.roles.addSuperAdmin'),
        value: UserRoles.SUPER_ADMIN,
        selected: ref(false),
        requested: ref(false),
        givenByAdmin: ref(false),
        newlyRequested: ref(false),
      },
      {
        label: i18n.t('auth.roles.addAdminFonctionnel'),
        value: UserRoles.ADMIN_FONCTIONNEL,
        selected: ref(false),
        requested: ref(false),
        givenByAdmin: ref(false),
        newlyRequested: ref(false),
      },
      {
        label: i18n.t('auth.roles.addLecteurCentral'),
        value: UserRoles.LECTEUR_CENTRAL,
        selected: ref(false),
        requested: ref(false),
        givenByAdmin: ref(false),
        newlyRequested: ref(false),
      },
      {
        label: i18n.t('auth.roles.addAdminTechniqueLocal'),
        value: UserRoles.ADMIN_TECHNIQUE_LOCAL,
        selected: ref(false),
        requested: ref(false),
        givenByAdmin: ref(false),
        newlyRequested: ref(false),
      },
      {
        label: i18n.t('auth.roles.addAdminCommune'),
        value: UserRoles.ADMIN_COMMUNE,
        selected: ref(false),
        requested: ref(false),
        givenByAdmin: ref(false),
        newlyRequested: ref(false),
      },
      {
        label: i18n.t('auth.roles.addAgentCommune'),
        value: UserRoles.AGENT_COMMUNE,
        selected: ref(false),
        requested: ref(false),
        givenByAdmin: ref(false),
        newlyRequested: ref(false),
      },
      {
        label: i18n.t('auth.roles.addLecteurLocal'),
        value: UserRoles.LECTEUR_LOCAL,
        selected: ref(false),
        requested: ref(false),
        givenByAdmin: ref(false),
        newlyRequested: ref(false),
      },
    ]
  }
  static getSignUpForm() {
    const baseSchema = this.getSchema().pick({
      userPrenom: true,
      userNom: true,
      userEmail: true,
      userLogin: true,
      confirmUserLogin: true,
    })

    const signUpSchema = baseSchema.refine((data) => data.userLogin === data.confirmUserLogin, {
      message: i18n.t('auth.password.notMatch') || 'Passwords do not match',
      path: ['confirmUserLogin'],
    })

    const { errors, handleSubmit, isSubmitting, meta } = useForm({
      validationSchema: toTypedSchema(signUpSchema),
    })

    const form = {
      userPrenom: useField<string>('userPrenom', '', { validateOnValueUpdate: false }),
      userNom: useField<string>('userNom', '', { validateOnValueUpdate: false }),
      userEmail: useField<string>('userEmail', '', { validateOnValueUpdate: false }),
      userLogin: useField<string>('userLogin', '', { validateOnValueUpdate: false }),
      confirmUserLogin: useField<string>('confirmUserLogin', '', { validateOnValueUpdate: false }),
    }

    return { form, errors, handleSubmit, isSubmitting, meta }
  }

  /*  static getSignUpThanksForm() {
    const baseSchema = this.getSchema().pick({
      organisation: true,
      position: true,
      phone: true,
      signUpMessage: true,
    })

    const { errors, handleSubmit, isSubmitting } = useForm({
      validationSchema: toTypedSchema(baseSchema),
    })
    const form = {
      organisation: useField('organisation', '', { validateOnValueUpdate: false }),
      position: useField('position', '', { validateOnValueUpdate: false }),
      phone: useField('phone', '', { validateOnValueUpdate: false }),
      signUpMessage: useField('signUpMessage', '', { validateOnValueUpdate: false }),
    }
    return { form, errors, handleSubmit, isSubmitting }
  } */

  static getUserEditionForm(userToEdit: User | null) {
    const baseSchema = this.getSchema().pick({
      userPrenom: true,
      userNom: true,
      userEmail: true,
      description: true,
    })
    const { errors, handleSubmit, isSubmitting, setValues, meta } = useForm({
      initialValues: userToEdit,
      validationSchema: toTypedSchema(baseSchema),
    })

    const form = {
      userPrenom: useField<string>('userPrenom', '', { validateOnValueUpdate: false }),
      userNom: useField<string>('userNom', '', { validateOnValueUpdate: false }),
      userEmail: useField<string>('userEmail', '', { validateOnValueUpdate: false }),
      description: useField<string>('description', '', { validateOnValueUpdate: false }),
    }

    return { form, errors, handleSubmit, isSubmitting, setValues, meta }
  }
}
